// src/mobile/navigation.ts
import {
  EventManager,
  setupEventListenersIfNeeded,
  WILDCARD_ACTION_ID,
} from './event-manager';
import {
  NavigationBarConfig,
  NavigationItem,
  MoreMenuGroup,
  NavigationEventType,
} from './types';
import { postJsMessage } from '../../../bridge';
import { NativeAPIResponseMessage } from '@zoom/hybrid-jsbridge';
import API from '../../../api';
import { isAvailable } from '../../utils';
import { PostMessageOption } from '../../../types';

function processMenuItems(items: NavigationItem[]): NavigationItem[] {
  return items.map((item) => {
    if (item.onTap) {
      const eventType: NavigationEventType = 'onMoreMenuClick';
      const actionID = item.actionID;
      EventManager.getInstance().registerCallback(
        eventType,
        actionID,
        item.onTap
      );
      return { ...item, actionID };
    }
    return item;
  });
}

function processMenuGroups(groups: MoreMenuGroup[]): MoreMenuGroup[] {
  return groups.map((group) => ({
    ...group,
    items: processMenuItems(group.items),
  }));
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.setNavigationBarConfig
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.5.0
 * android    6.5.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function setNavigationBarConfig(
  config: NavigationBarConfig,
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<unknown>> {
  await isAvailable('mobile.setNavigationBarConfig');
  setupEventListenersIfNeeded();
  const processedConfig = { ...config };

  if (processedConfig.search?.sourceType) {
    processedConfig.search.sourceType = JSON.stringify(
      processedConfig.search.sourceType
    );
  }

  // Process menu groups if they exist
  if (processedConfig.moreMenuContent?.itemsGroup) {
    processedConfig.moreMenuContent.itemsGroup = processMenuGroups(
      processedConfig.moreMenuContent.itemsGroup
    );
  }

  if (processedConfig.primaryNavigationItem?.onTap) {
    const eventType: NavigationEventType = 'onPrimaryNavigationItemClick';
    EventManager.getInstance().unregisterCallback(
      eventType,
      WILDCARD_ACTION_ID
    );

    // Register new callback
    EventManager.getInstance().registerCallback(
      eventType,
      WILDCARD_ACTION_ID,
      processedConfig.primaryNavigationItem.onTap
    );

    // Remove from config
    delete processedConfig.primaryNavigationItem.onTap;
  }

  // Define event handlers to process
  const eventHandlers = {
    onMoreMenuClick: 'onMoreMenuClick',
    onAvatarClick: 'onAvatarClick',
    onSearchBegin: 'onSearchBegin',
    onSearchTextChanged: 'onSearchTextChanged',
    onSearchDone: 'onSearchDone',
    onSearchCancel: 'onSearchCancel',
  } as const;

  // Process all event handlers in a loop
  for (const [configKey, eventName] of Object.entries(eventHandlers)) {
    if (processedConfig[configKey as keyof typeof eventHandlers]) {
      const handler = processedConfig[configKey as keyof typeof eventHandlers];

      // Cleanup old callback
      EventManager.getInstance().unregisterCallback(
        eventName,
        WILDCARD_ACTION_ID
      );

      // Register new callback
      EventManager.getInstance().registerCallback(
        eventName,
        WILDCARD_ACTION_ID,
        handler as any
      );

      // Remove from config
      delete processedConfig[configKey as keyof typeof eventHandlers];
    }
  }

  return postJsMessage(API.setNavigationBarConfig, processedConfig, options);
}
